import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FiX, FiMapPin, FiVideo } from 'react-icons/fi';
import { useAppSelector } from '../../app/hooks';
import styles from './RequestMeetingModal.module.css';
import axiosClient from '../../api/axiosClient';

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
}

const RequestMeetingModal: React.FC<Props> = ({ onClose, onSuccess }) => {
  const { user } = useAppSelector((state) => state.auth);
  const [meetingType, setMeetingType] = useState<'physical' | 'vc'>('vc');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!user?.full_name || !user?.email) {
      toast.error('Could not read your account details — please refresh and try again.');
      return;
    }
    setSaving(true);
    try {
      await axiosClient.post('/leads', {
        leadType: 'meeting',
        meetingType,
        fullName: user.full_name,
        email: user.email,
        phone: user.phone || undefined,
        fundOrg: user.fund_org || undefined,
        message: message.trim() || undefined,
      });
      toast.success('Meeting request sent — our team will reach out shortly.');
      onSuccess?.();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not submit your request.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h2>Request a Meeting</h2>
            <p>Our team will reach out to schedule a time that works for you.</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close"><FiX /></button>
        </div>

        <div className={styles.body}>
          <label className={styles.label}>Meeting Type</label>
          <div className={styles.typeToggle}>
            <button type="button" className={meetingType === 'vc' ? styles.typeActive : ''} onClick={() => setMeetingType('vc')}>
              <FiVideo /> Virtual / VC call
            </button>
            <button type="button" className={meetingType === 'physical' ? styles.typeActive : ''} onClick={() => setMeetingType('physical')}>
              <FiMapPin /> In-person
            </button>
          </div>

          <label className={styles.label}>Anything you'd like us to know? (optional)</label>
          <textarea
            className={styles.textarea}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Topics you'd like to cover, preferred timing, etc."
            rows={4}
          />

          <div className={styles.contactPreview}>
            Requesting as <b>{user?.full_name}</b> ({user?.email})
          </div>

          <button className={styles.confirmBtn} onClick={handleSubmit} disabled={saving}>
            {saving ? 'Sending…' : 'Send Request'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestMeetingModal;
